/**
 * Safe localStorage utilities with error handling
 * Prevents memory leaks from corrupted data and handles edge cases
 */

/**
 * Safely get and parse JSON from localStorage
 * @param {string} key - localStorage key
 * @param {*} defaultValue - default value if key doesn't exist or parsing fails
 * @returns {*} parsed value or defaultValue
 */
export const getJSON = (key, defaultValue = null) => {
  try {
    const item = localStorage.getItem(key);
    if (item === null) return defaultValue;
    return JSON.parse(item);
  } catch (error) {
    console.error(`Failed to parse JSON from localStorage key "${key}":`, error);
    return defaultValue;
  }
};

/**
 * Safely set JSON to localStorage
 * @param {string} key - localStorage key
 * @param {*} value - value to stringify and store
 * @returns {boolean} success status
 */
export const setJSON = (key, value) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
    return true;
  } catch (error) {
    console.error(`Failed to set JSON to localStorage key "${key}":`, error);
    return false;
  }
};

/**
 * Safely get string from localStorage
 * @param {string} key - localStorage key
 * @param {string} defaultValue - default value if key doesn't exist
 * @returns {string} value or defaultValue
 */
export const getString = (key, defaultValue = '') => {
  try {
    const item = localStorage.getItem(key);
    return item !== null ? item : defaultValue;
  } catch (error) {
    console.error(`Failed to get from localStorage key "${key}":`, error);
    return defaultValue;
  }
};

/**
 * Safely remove item from localStorage
 * @param {string} key - localStorage key
 * @returns {boolean} success status
 */
export const removeItem = (key) => {
  try {
    localStorage.removeItem(key);
    return true;
  } catch (error) {
    console.error(`Failed to remove localStorage key "${key}":`, error);
    return false;
  }
};

/**
 * Safely clear all localStorage
 * @returns {boolean} success status
 */
export const clearAll = () => {
  try {
    localStorage.clear();
    return true;
  } catch (error) {
    console.error('Failed to clear localStorage:', error);
    return false;
  }
};

/**
 * Get boolean from localStorage
 * @param {string} key - localStorage key
 * @param {boolean} defaultValue - default value
 * @returns {boolean}
 */
export const getBoolean = (key, defaultValue = false) => {
  try {
    const item = localStorage.getItem(key);
    if (item === null) return defaultValue;
    return item === 'true';
  } catch (error) {
    console.error(`Failed to get boolean from localStorage key "${key}":`, error);
    return defaultValue;
  }
};

/**
 * Get number from localStorage
 * @param {string} key - localStorage key
 * @param {number} defaultValue - default value
 * @returns {number}
 */
export const getNumber = (key, defaultValue = 0) => {
  try {
    const item = localStorage.getItem(key);
    if (item === null) return defaultValue;
    const num = Number(item);
    return isNaN(num) ? defaultValue : num;
  } catch (error) {
    console.error(`Failed to get number from localStorage key "${key}":`, error);
    return defaultValue;
  }
};

const safeLocalStorage = {
  getJSON,
  setJSON,
  getString,
  removeItem,
  clearAll,
  getBoolean,
  getNumber,
};

export default safeLocalStorage;
